// ============================================================================
// _meta.widget renderer — rich display widgets attached to session updates
// ============================================================================
// Agents attach "_meta":{"widget":{...}} to tool_call / tool_call_update /
// agent_message_chunk updates (see the [gateway-ui] preamble injected into
// the first prompt of each session). This module whitelists and renders a
// small set of widget types; anything unknown or malformed returns false so
// the caller falls back to its normal rendering. All payloads are treated
// as untrusted agent output: only whitelisted keys are read, everything is
// rendered via textContent/DOM APIs (no innerHTML with payload text), and
// URLs are restricted to http/https.
//
// Supported types: kv, table, link, progress, code, markdown, actions.

import { renderMarkdown } from "./markdown.js";
import { wireFileLinks } from "./file-viewer.js";
import { addCopyButtons } from "./copy.js";
import { addPinButtons } from "./pins.js";

// ---- deps (set by initWidgets) ----
let sendFn = null;

export function initWidgets(deps) {
  sendFn = deps.send;
}

const MAX_ROWS = 100;
const MAX_ITEMS = 50;
const MAX_TEXT = 64 * 1024;
const MAX_ACTIONS = 8;

function str(v, max = MAX_TEXT) {
  return typeof v === "string" ? v.slice(0, max) : "";
}

function clampInt(v, lo, hi) {
  const n = Math.round(Number(v));
  return Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : null;
}

/** Whitelist + normalize a raw _meta.widget payload. Returns spec or null. */
export function normalizeWidget(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const title = str(raw.title, 200) || undefined;
  switch (raw.type) {
    case "kv": {
      const items = [];
      if (Array.isArray(raw.items)) {
        for (const it of raw.items.slice(0, MAX_ITEMS)) {
          if (Array.isArray(it) && it.length >= 2) {
            items.push([str(it[0], 300), str(it[1], 1000)]);
          } else if (it && typeof it === "object" && it.key != null) {
            items.push([str(it.key, 300), str(it.value, 1000)]);
          }
        }
      }
      return items.length ? { type: "kv", title, items } : null;
    }
    case "table": {
      const columns = Array.isArray(raw.columns)
        ? raw.columns.slice(0, 20).map((c) => str(c, 200))
        : [];
      const rows = Array.isArray(raw.rows)
        ? raw.rows.slice(0, MAX_ROWS).map((r) =>
            Array.isArray(r) ? r.slice(0, 20).map((c) => str(c, 1000)) : [])
        : [];
      return columns.length && rows.length ? { type: "table", title, columns, rows } : null;
    }
    case "link": {
      const url = str(raw.url, 2048);
      if (!/^https?:\/\//i.test(url)) return null;
      return { type: "link", title, url, description: str(raw.description, 500) || undefined };
    }
    case "progress": {
      const percent = clampInt(raw.percent, 0, 100);
      if (percent === null) return null;
      return { type: "progress", title, percent, label: str(raw.label, 200) || undefined };
    }
    case "code": {
      const code = str(raw.code);
      if (!code) return null;
      return { type: "code", title, code, language: str(raw.language, 40) || undefined };
    }
    case "markdown": {
      const text = str(raw.text);
      if (!text) return null;
      return { type: "markdown", title, text };
    }
    case "actions": {
      const actions = [];
      if (Array.isArray(raw.actions)) {
        for (const a of raw.actions.slice(0, MAX_ACTIONS)) {
          if (a && typeof a === "object" && typeof a.label === "string" && typeof a.prompt === "string") {
            actions.push({ label: a.label.slice(0, 80), prompt: a.prompt.slice(0, 2000) });
          }
        }
      }
      return actions.length ? { type: "actions", title, actions } : null;
    }
    default:
      return null;
  }
}

function el(tag, className, text) {
  const d = document.createElement(tag);
  if (className) d.className = className;
  if (text != null) d.textContent = text;
  return d;
}

function renderWidgetBody(card, spec) {
  switch (spec.type) {
    case "kv": {
      const table = el("table", "w-kv");
      const tbody = document.createElement("tbody");
      for (const [k, v] of spec.items) {
        const tr = document.createElement("tr");
        tr.appendChild(el("td", "w-kv-k", k));
        tr.appendChild(el("td", "w-kv-v", v));
        tbody.appendChild(tr);
      }
      table.appendChild(tbody);
      card.appendChild(table);
      break;
    }
    case "table": {
      const wrap = el("div", "w-table-scroll");
      const table = el("table", "w-table");
      const thead = document.createElement("thead");
      const htr = document.createElement("tr");
      for (const c of spec.columns) htr.appendChild(el("th", null, c));
      thead.appendChild(htr);
      table.appendChild(thead);
      const tbody = document.createElement("tbody");
      for (const r of spec.rows) {
        const tr = document.createElement("tr");
        for (let i = 0; i < spec.columns.length; i++) {
          tr.appendChild(el("td", null, r[i] ?? ""));
        }
        tbody.appendChild(tr);
      }
      table.appendChild(tbody);
      wrap.appendChild(table);
      card.appendChild(wrap);
      break;
    }
    case "link": {
      const a = el("a", "w-link", spec.title || spec.url);
      a.href = spec.url;
      a.target = "_blank";
      a.rel = "noopener";
      card.appendChild(a);
      if (spec.description) card.appendChild(el("div", "w-link-desc", spec.description));
      break;
    }
    case "progress": {
      const wrap = el("div", "w-progress");
      const bar = el("div", "w-progress-bar");
      const fill = el("div", "w-progress-fill");
      fill.style.width = spec.percent + "%";
      bar.appendChild(fill);
      wrap.appendChild(bar);
      wrap.appendChild(el("span", "w-progress-label", spec.label ?? spec.percent + "%"));
      card.appendChild(wrap);
      break;
    }
    case "code": {
      const pre = document.createElement("pre");
      pre.className = "w-code";
      const code = document.createElement("code");
      if (spec.language && typeof hljs !== "undefined" && hljs.getLanguage(spec.language)) {
        code.innerHTML = hljs.highlight(spec.code, { language: spec.language }).value;
      } else {
        code.textContent = spec.code;
      }
      pre.appendChild(code);
      card.appendChild(pre);
      addCopyButtons(card);
      addPinButtons(card);
      break;
    }
    case "markdown": {
      const body = el("div", "w-markdown");
      body.innerHTML = renderMarkdown(spec.text);
      wireFileLinks(body);
      card.appendChild(body);
      addCopyButtons(body);
      addPinButtons(body);
      break;
    }
    case "actions": {
      const wrap = el("div", "w-actions");
      for (const a of spec.actions) {
        const chip = el("button", "action-chip", a.label);
        chip.type = "button";
        chip.onclick = () => {
          if (sendFn) sendFn({ type: "prompt", text: a.prompt });
        };
        wrap.appendChild(chip);
      }
      card.appendChild(wrap);
      break;
    }
  }
}

/**
 * Render one normalized spec (or raw payload) into `container` as a
 * .widget-card. Returns true if rendered, false for unknown/malformed.
 */
export function renderWidget(container, raw) {
  const spec = normalizeWidget(raw);
  if (!spec) return false;
  const card = el("div", "widget-card w-" + spec.type + "-card");
  if (spec.title) card.appendChild(el("div", "w-title", spec.title));
  renderWidgetBody(card, spec);
  container.appendChild(card);
  return true;
}

/**
 * Scan an update's `_meta` for widget payloads — `update._meta.widget`
 * plus each content item's `_meta.widget` — and render each into
 * `container`. Widget payloads may be a single object or an array.
 * Returns the number of widgets rendered.
 */
export function renderMetaWidgets(container, update) {
  let count = 0;
  const specs = [];
  const top = update?._meta?.widget;
  if (top) specs.push(...(Array.isArray(top) ? top : [top]));
  if (Array.isArray(update?.content)) {
    for (const item of update.content) {
      const w = item?._meta?.widget ?? item?.content?._meta?.widget;
      if (w) specs.push(...(Array.isArray(w) ? w : [w]));
    }
  }
  for (const s of specs) {
    if (renderWidget(container, s)) count++;
  }
  return count;
}
